import { Loader } from "@googlemaps/js-api-loader"
import { Controller } from "@hotwired/stimulus"
import { GoogleMapsLoader } from "./map/google_maps_loader"
import { PinMapHandler } from "./map/pin_map_handler"
import { FenceDrawingHandler } from "./map/fence_drawing_handler"
import { MapSearchHandler } from "./map/map_search_handler"

export default class extends Controller {
  static values = {
    apiKey: String,
    typeMap: { type: String, default: 'pin' },
    mode: { type: String, default: 'none' },
    lat: Number,
    lng: Number,
    fenceType: { type: String, default: 'polygon' }
  }

  static targets = ['map', 'lat', 'lng', 'search', 'coords', 'shape', 'pull', 'color_picker', 'button', 'modalTitle', 'footerInstruction', 'instruction', 'openMapBtn', 'mapTypeDropdown', 'mapTypeMenu', 'mapTypeText', 'undoBtn', 'finishBtn', 'restartBtn', 'drawingHint']

  initialize() {
    // O formulario de cerca declara um controller `map` sem alvo de mapa (o
    // mapa vive no modal, que tem o seu proprio). Sem esta guarda os dois
    // carregam a Google Maps API e disputam window.mapController.
    if (!this.hasMapTarget) return

    this.setupInitialState()
    this.initializeLoaders()
    this.initializeHandlers()
    this.loadGoogleMaps()
    this.setupEventListeners()
  }

  connect() {
    if (!this._initialized) {
      this.initialize()
      this._initialized = true
    }
  }

  setupInitialState() {
    this.editMode = null
    this.markers = []
    this.path = []
    this.searchBox = null
    this.mapInitialized = false
    this.isEditMode = this.modeValue === 'edit'
    this.currentMapType = 'roadmap'

    const fenceTypeSelect = document.getElementById('fence_type')
    if (fenceTypeSelect?.value && fenceTypeSelect.value !== 'prompt') {
      this.fenceTypeValue = fenceTypeSelect.value
    }

    window.mapController = this
  }

  initializeLoaders() {
    this._loader = new Loader({
      apiKey: this.apiKeyValue,
      libraries: ["maps", "places", "geometry"],
      version: "weekly"
    })

    this._gMaps = this._loader.importLibrary('maps')
    this._gMarker = this._loader.importLibrary('marker')
  }

  initializeHandlers() {
    this.loader = new GoogleMapsLoader(this.apiKeyValue)
    this.pinHandler = new PinMapHandler(this)
    this.searchHandler = new MapSearchHandler(this)
  }

  setupEventListeners() {
    const fenceTypeSelect = document.getElementById('fence_type')
    if (fenceTypeSelect) {
      fenceTypeSelect.addEventListener('change', (e) => {
        this.setFenceType(e.target.value)
      })
    }
  }

  async loadGoogleMaps() {
    try {
      await this.loader.load()

      switch (this.typeMapValue) {
        case 'pin':
          this.initPinMap()
          break
        case 'fence':
          await this.initFenceMap()
          break
      }
    } catch (error) {
      this.showMapError(`Erro: ${error.message}. Verifique a API Key.`)
    }
  }

  initPinMap() {
    this._gMaps.then(({ Map }) => {
      if (this.hasMapTarget) {
        if (this.latValue && this.lngValue && this.modeValue == 'none') {
          this.map = new Map(this.mapTarget, { zoom: 10, center: { lat: this.latValue, lng: this.lngValue } })

          this._gMarker.then(({ Marker }) => {
            this._marker = new Marker({ position: { lat: this.latValue, lng: this.lngValue }, map: this.map, editable: false, visible: true })
          })
        } else {
          if (this.modeValue == 'edit') {
            this.map = new Map(this.mapTarget, { zoom: 6, center: { lat: this.latValue, lng: this.lngValue } })

            this._gMarker.then(({ Marker }) => {
              this._marker = new Marker({ position: { lat: this.latValue, lng: this.lngValue }, map: this.map, editable: true, visible: true })
            })
            this.map.addListener('click', this.onMapClick.bind(this))
          } else {
            this.map = new Map(this.mapTarget, { zoom: 4, center: { lat: -14, lng: -55 } })
            this.map.addListener('click', this.onMapClick.bind(this))
          }
        }
      }
    })
  }

  async initFenceMap() {
    this.editMode = /edit|new/.test(document.location.href) || this.modeValue === 'edit'

    if (!this.hasMapTarget) return

    this.map = new google.maps.Map(this.mapTarget, {
      zoom: 4,
      center: { lat: -14, lng: -55 },
      mapTypeControl: false, // Desabilita o controle nativo
      streetViewControl: false,
      fullscreenControl: false, // Desabilitado - usaremos nosso próprio
      zoomControl: false, // Desabilitado - usaremos nosso próprio
      rotateControl: false, // Desabilitado para manter limpo
      scaleControl: false, // Desabilitado para manter limpo
      mapTypeId: google.maps.MapTypeId[this.currentMapType.toUpperCase()]
    })

    this.fenceHandler = new FenceDrawingHandler(this, this.map)
    await this.fenceHandler.init()

    if (this.hasSearchTarget) {
      this.searchHandler.init()
    }

    this.updateInstructions()
    this.mapInitialized = true

    // Inicializar botões de controle
    this.initializeControlButtons()

    // Carregar coordenadas existentes automaticamente no modo show
    if (this.modeValue === 'show') {
      setTimeout(() => {
        this.loadExistingCoords()
      }, 1000)
    }
  }

  onMapClick(event) {
    if (this.typeMapValue === 'pin') {
      this.pinHandler?.handleClick(event)
    }
  }

  setFenceType(type) {
    if (!type || type === 'prompt' || type === '') {
      console.log('❌ Tipo inválido, não fazendo nada')
      return
    }

    // console.log('🧹 Limpando área ao mudar tipo de cerca')
    // this.clearAllAreaData()

    this.fenceTypeValue = type
    console.log('✅ Novo tipo definido:', this.fenceTypeValue)

    if (this.fenceHandler) {
      this.fenceHandler.clearAll()
      this.updateInstructions()
    }

    this.notifyFenceControllerAreaCleared()
  }

  startDrawing() {
    if (this.fenceHandler && this.fenceTypeValue && this.fenceTypeValue !== 'prompt') {
      this.fenceHandler.setDrawingMode(this.fenceTypeValue)
      this.updateInstructions()
    }
  }

  stopDrawing() {
    if (this.fenceHandler) {
      this.fenceHandler.stopDrawing()
    }
  }

  undoVertex() {
    this.fenceHandler?.undoLastVertex()
  }

  finishShape() {
    this.fenceHandler?.finishShape()
  }

  restartShape() {
    this.fenceHandler?.restartDrawing()
  }

  // Chamado pelo FenceDrawingHandler a cada mudança de estado do traçado.
  onDrawingStateChange(state) {
    this._drawingState = state
    this._syncControlButtons(state)
    this.updateInstructions()
  }

  _syncControlButtons(state) {
    if (this.hasUndoBtnTarget) this._toggleButton(this.undoBtnTarget, state.canUndo)
    if (this.hasFinishBtnTarget) this._toggleButton(this.finishBtnTarget, state.canFinish)
    if (this.hasRestartBtnTarget) this._toggleButton(this.restartBtnTarget, state.drawing || state.hasShape)
  }

  _toggleButton(button, enabled) {
    button.disabled = !enabled
    button.classList.toggle('opacity-40', !enabled)
    button.classList.toggle('cursor-not-allowed', !enabled)
  }

  reactivateDrawing() {
    setTimeout(() => {
      if (this.fenceTypeValue && this.fenceTypeValue !== 'prompt' && this.fenceTypeValue !== '') {
        this.startDrawing()
      }
    }, 200)
  }

  t(key, replacements = {}) {
    const locale = window.I18n?.locale || 'pt_BR'
    const template = window.I18n?.translations?.[locale]?.javascript?.fence_drawing?.[key]
    if (!template) return key

    return Object.entries(replacements).reduce(
      (text, [name, value]) => text.replaceAll(`%{${name}}`, value),
      template
    )
  }

  updateInstructions() {
    const isCircle = this.fenceTypeValue === 'circle'

    if (this.hasModalTitleTarget) {
      this.modalTitleTarget.textContent = isCircle ? this.t('title_circle') : this.t('title_polygon')
    }

    const hint = isCircle ? this._circleHint() : this._polygonHint()

    if (this.hasFooterInstructionTarget) this.footerInstructionTarget.textContent = hint
    if (this.hasDrawingHintTarget) this.drawingHintTarget.textContent = hint
  }

  _polygonHint() {
    const state = this._drawingState

    if (!state?.drawing) return this.t(state?.hasShape ? 'polygon_done' : 'polygon_start')
    if (state.vertexCount === 0) return this.t('polygon_start')
    if (state.vertexCount < 3) {
      return this.t('polygon_need_more', { count: state.vertexCount, missing: 3 - state.vertexCount })
    }

    return this.t('polygon_can_finish', { count: state.vertexCount })
  }

  _circleHint() {
    const state = this._drawingState

    if (!state?.drawing) return this.t(state?.hasShape ? 'circle_done' : 'circle_start')
    if (!state.hasCenter) return this.t('circle_start')

    return this.t('circle_radius', { radius: this.constructor.formatDistance(state.radius) })
  }

  static formatDistance(meters) {
    if (!meters || meters < 1) return '0 m'
    if (meters < 1000) return `${Math.round(meters)} m`

    return `${(meters / 1000).toFixed(meters < 10000 ? 2 : 1)} km`
  }

  initializeControlButtons() {
    // Inicializar dropdown de tipo de mapa
    this.updateMapTypeText(this.currentMapType)
  }


  // O color picker vive no formulario, cujo controller nao monta mapa: o
  // handler a repintar e o do modal.
  changeColor() {
    const handler = this.fenceHandler || window.mapController?.fenceHandler
    handler?.updateColor(this.getCurrentColor())
  }

  clearAll() {
    if (this.fenceHandler) {
      this.fenceHandler.clearAll()
    }
    this.path = []
    this.markers = []
  }

  loadExistingCoords() {
    if (this.fenceHandler) {
      this.fenceHandler.loadExistingCoords()
    }
  }

  getCurrentColor() {
    const colorInput = document.querySelector('input[name="electronic_fence[color]"]')
    return colorInput ? colorInput.value : '#3260a8'
  }

  notifyFenceControllerAreaCleared() {
    const fenceElement = document.querySelector('[data-controller*="electronic-fence"]')
    if (fenceElement?.electronicFence) {
      fenceElement.electronicFence.areaSelected = false
      if (fenceElement.electronicFence.hideAreaStatus) {
        fenceElement.electronicFence.hideAreaStatus()
      }
      if (fenceElement.electronicFence.clearAreaError) {
        fenceElement.electronicFence.clearAreaError()
      }
    }
  }

  showMapError(message) {
    if (this.hasMapTarget) {
      this.mapTarget.innerHTML = `
        <div class="flex items-center justify-center h-full bg-red-50 border-2 border-red-200 rounded-lg">
          <div class="text-center p-8">
            <div class="text-6xl mb-4">❌</div>
            <h3 class="text-xl font-bold text-red-700 mb-2">Erro ao carregar Google Maps</h3>
            <p class="text-red-600">${message}</p>
            <button onclick="location.reload()" class="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
              Tentar Novamente
            </button>
          </div>
        </div>
      `
    }
  }

  // Métodos para controle de tipo de mapa (dropdown)
  toggleMapTypeDropdown(event) {
    event.stopPropagation()
    if (this.hasMapTypeMenuTarget) {
      this.mapTypeMenuTarget.classList.toggle('hidden')
    }
    
    
    // Fechar dropdown ao clicar fora
    document.addEventListener('click', this.closeDropdown.bind(this), { once: true })
  }

  selectMapType(event) {
    const mapType = event.target.dataset.mapType
    this.currentMapType = mapType
    
    if (this.map) {
      this.map.setMapTypeId(google.maps.MapTypeId[mapType.toUpperCase()])
    }
    
    this.updateMapTypeText(mapType)
    this.mapTypeMenuTarget.classList.add('hidden')
  }

  updateMapTypeText(mapType) {
    const typeNames = {
      'roadmap': 'Ruas',
      'satellite': 'Satélite', 
      'hybrid': 'Híbrido'
    }
    
    if (this.hasMapTypeTextTarget) {
      this.mapTypeTextTarget.textContent = typeNames[mapType] || 'Ruas'
    }
  }

  closeDropdown() {
    if (this.hasMapTypeMenuTarget) {
      this.mapTypeMenuTarget.classList.add('hidden')
    }
  }


}